import { apiClient } from './api'
import {
  User,
  AuthResponse,
  LoginFormData,
  RegisterFormData,
  APIKeyLoginFormData,
  ForgotPasswordFormData,
  ResetPasswordFormData,
  ChangePasswordFormData,
  UpdateProfileFormData,
} from '@/types'

/**
 * Comprehensive Authentication Service
 * Handles all authentication-related API calls with proper error handling
 */
export const authService = {
  // Standard Authentication
  async login(data: LoginFormData): Promise<AuthResponse> {
    return apiClient.post('/auth/login/', data)
  },

  async register(data: RegisterFormData): Promise<AuthResponse> {
    return apiClient.post('/auth/register/', data)
  },

  async logout(): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/logout/')
  },

  // API Key Authentication
  async loginWithAPIKey(data: APIKeyLoginFormData): Promise<AuthResponse> {
    return apiClient.post('/auth/api-key/login/', data)
  },

  async logoutAPIKey(): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/api-key/logout/')
  },

  // Token Management
  async refreshToken(
    refreshToken: string
  ): Promise<{ success: boolean; tokens: { access: string; refresh: string } }> {
    return apiClient.post('/auth/refresh/', { refresh: refreshToken })
  },

  // Email Verification
  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    return apiClient.get(`/auth/verify-email/?token=${token}`)
  },

  async resendVerificationEmail(): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/resend-verification/')
  },

  // Password Management
  async forgotPassword(
    data: ForgotPasswordFormData
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/forgot-password/', data)
  },

  async resetPassword(data: ResetPasswordFormData): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/reset-password/', data)
  },

  async changePassword(
    data: ChangePasswordFormData
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/auth/change-password/', data)
  },

  // Profile Management
  async getProfile(): Promise<{ success: boolean; user: User }> {
    return apiClient.get('/auth/profile/')
  },

  async updateProfile(
    data: UpdateProfileFormData
  ): Promise<{ success: boolean; message: string; user: User }> {
    return apiClient.patch('/auth/profile/', data)
  },

  // Utility Methods
  async checkAuthStatus(): Promise<boolean> {
    try {
      const response = await this.getProfile()
      return response.success
    } catch (error) {
      return false
    }
  },

  // Error Handling Helper
  handleAuthError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors.join(', ')
    }
    return error.message || 'An unexpected error occurred'
  },
}
